import { z } from 'zod';

/**
 * Personal assistant — unified calendar, attention feed and tasks across every
 * account the owner has (Microsoft 365 across tenants, Google, plain IMAP).
 *
 * Nothing here is company-scoped. Every row belongs to the signed-in owner
 * (Entra `oid`), and the API namespace is `/api/v1/assistant/...`.
 */

export const ASSISTANT_PROVIDERS = ['microsoft', 'google', 'imap', 'carries'] as const;
export type AssistantProviderKey = (typeof ASSISTANT_PROVIDERS)[number];

/** Providers that connect through an OAuth authorization-code flow. */
export const ASSISTANT_OAUTH_PROVIDERS = ['microsoft', 'google'] as const;
export type AssistantOAuthProviderKey = (typeof ASSISTANT_OAUTH_PROVIDERS)[number];

export const CONNECTION_STATUSES = ['active', 'needs_reauth', 'disabled', 'error'] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const SYNC_SCOPES = ['calendar', 'mail', 'tasks'] as const;
export type SyncScope = (typeof SYNC_SCOPES)[number];

export const SYNC_RUN_STATUSES = ['running', 'ok', 'partial', 'error'] as const;
export type SyncRunStatus = (typeof SYNC_RUN_STATUSES)[number];

export const SYNC_TRIGGERS = ['scheduler', 'manual', 'writeback', 'connect'] as const;
export type SyncTrigger = (typeof SYNC_TRIGGERS)[number];

export const EVENT_STATUSES = ['confirmed', 'tentative', 'cancelled'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export const ATTENTION_STATES = [
  'needs_reply',
  'awaiting_reply',
  'flagged',
  'deadline',
  'vip',
  'none',
] as const;
export type AttentionState = (typeof ATTENTION_STATES)[number];

export const MESSAGE_LOCAL_STATES = ['open', 'done', 'snoozed'] as const;
export type MessageLocalState = (typeof MESSAGE_LOCAL_STATES)[number];

export const TASK_PROVIDERS = ['microsoft', 'google', 'carries', 'native'] as const;
export type TaskProviderKey = (typeof TASK_PROVIDERS)[number];

/** Default pull cadence. The owner asked for three hours, not minutes. */
export const DEFAULT_SYNC_INTERVAL_SEC = 3 * 60 * 60;
export const MIN_SYNC_INTERVAL_SEC = 15 * 60;
export const MAX_SYNC_INTERVAL_SEC = 24 * 60 * 60;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a #RRGGBB colour');

// ── Connections ────────────────────────────────────────────────────────────

export const oauthStartSchema = z.object({
  provider: z.enum(ASSISTANT_OAUTH_PROVIDERS),
  label: z.string().trim().min(1).max(80).optional(),
  /** Re-consent an existing connection (tokens replaced, rows kept). */
  reconnectConnectionId: z.string().uuid().optional(),
});
export type OAuthStartInput = z.infer<typeof oauthStartSchema>;

export const oauthCallbackSchema = z.object({
  code: z.string().min(1).max(4096),
  state: z.string().min(16).max(128),
});
export type OAuthCallbackInput = z.infer<typeof oauthCallbackSchema>;

export const updateConnectionSchema = z
  .object({
    label: z.string().trim().min(1).max(80),
    color: hexColor,
    status: z.enum(['active', 'disabled']),
    syncIntervalSec: z.number().int().min(MIN_SYNC_INTERVAL_SEC).max(MAX_SYNC_INTERVAL_SEC),
    /** Appended to replies and new mail sent from this account; plain text. */
    signature: z.string().max(2000).nullable(),
  })
  .partial();
export type UpdateConnectionInput = z.infer<typeof updateConnectionSchema>;

export interface AssistantConnection {
  id: string;
  provider: AssistantProviderKey;
  label: string;
  color: string;
  accountEmail: string | null;
  /** Microsoft tenant id — tells the two M365 tenants apart. */
  tenantId: string | null;
  capabilities: { calendar: boolean; mail: boolean; tasks: boolean };
  /** Plain-text signature appended to mail sent from this account. */
  signature: string | null;
  status: ConnectionStatus;
  lastError: string | null;
  consecutiveFailures: number;
  syncIntervalSec: number;
  nextSyncAt: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

export interface AssistantSyncRun {
  id: string;
  connectionId: string;
  scope: SyncScope | 'all';
  trigger: SyncTrigger;
  status: SyncRunStatus;
  startedAt: string;
  finishedAt: string | null;
  stats: Record<string, number>;
  error: string | null;
}

/** What the Connections page needs to render its provider cards. */
export interface AssistantStatus {
  /** False when no provider secret is configured at all — the whole section is off. */
  configured: boolean;
  providers: Record<AssistantOAuthProviderKey, { configured: boolean }> & {
    /** IMAP needs no secret: always available once the assistant is on at all. */
    imap: { configured: boolean };
    /** Carrie's bar app: reached with the API's own managed identity, no secret. */
    carries: { configured: boolean };
  };
  redirectUri: string | null;
}

// ── Calendars & events ─────────────────────────────────────────────────────

export const updateCalendarSchema = z
  .object({
    isSelected: z.boolean(),
    color: hexColor.nullable(),
  })
  .partial();
export type UpdateCalendarInput = z.infer<typeof updateCalendarSchema>;

export interface AssistantCalendar {
  id: string;
  connectionId: string;
  provider: AssistantProviderKey;
  connectionLabel: string;
  providerCalendarId: string;
  name: string;
  /** Effective colour: calendar override, else the connection colour. */
  color: string;
  timezone: string | null;
  isPrimary: boolean;
  isSelected: boolean;
  canWrite: boolean;
}

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const eventsQuerySchema = z.object({
  /** Inclusive start, YYYY-MM-DD, interpreted in the viewer's timezone by the client. */
  from: isoDate,
  /** Exclusive end, YYYY-MM-DD. */
  to: isoDate,
  /** Comma-separated connection ids; omitted = all. */
  connectionIds: z.string().optional(),
});
export type EventsQuery = z.infer<typeof eventsQuerySchema>;

export interface AssistantEvent {
  id: string;
  connectionId: string;
  calendarId: string;
  provider: AssistantProviderKey;
  color: string;
  providerEventId: string;
  providerSeriesId: string | null;
  subject: string;
  bodyPreview: string | null;
  location: string | null;
  /** ISO instant. For all-day events this is local midnight in the calendar's zone. */
  startsAt: string;
  endsAt: string;
  isAllDay: boolean;
  /** Present only for all-day events. `endDate` is exclusive, iCalendar style. */
  startDate: string | null;
  endDate: string | null;
  timezone: string | null;
  status: EventStatus;
  isOrganizer: boolean;
  responseStatus: string | null;
  attendees: { email: string | null; name: string | null; status: string | null }[];
  webLink: string | null;
  matterRef: string | null;
  /** Set while a local write is waiting for the next sync to confirm it. */
  pendingSync: boolean;
  /** The calendar accepts writes from here (provider permission and an active connection). */
  canWrite: boolean;
}

const attendeeInput = z.object({
  email: z.string().email(),
  name: z.string().trim().max(200).optional(),
});

export const createEventSchema = z
  .object({
    calendarId: z.string().uuid(),
    subject: z.string().trim().min(1).max(255),
    body: z.string().max(10_000).optional(),
    location: z.string().trim().max(500).optional(),
    isAllDay: z.boolean().default(false),
    /** Timed events: ISO instants. All-day events: YYYY-MM-DD, end exclusive. */
    start: z.string().min(10),
    end: z.string().min(10),
    /** IANA zone the event is expressed in; defaults to the calendar's zone. */
    timezone: z.string().max(64).optional(),
    attendees: z.array(attendeeInput).max(50).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.isAllDay) {
      if (!isoDate.safeParse(v.start).success || !isoDate.safeParse(v.end).success) {
        ctx.addIssue({ code: 'custom', message: 'All-day events use YYYY-MM-DD for start and end' });
      } else if (v.end <= v.start) {
        ctx.addIssue({ code: 'custom', message: 'end must be after start' });
      }
    } else {
      const s = Date.parse(v.start);
      const e = Date.parse(v.end);
      if (Number.isNaN(s) || Number.isNaN(e)) {
        ctx.addIssue({ code: 'custom', message: 'start and end must be ISO timestamps' });
      } else if (e <= s) {
        ctx.addIssue({ code: 'custom', message: 'end must be after start' });
      }
    }
  });
export type CreateEventInput = z.infer<typeof createEventSchema>;

export const updateEventSchema = z
  .object({
    subject: z.string().trim().min(1).max(255),
    body: z.string().max(10_000).nullable(),
    location: z.string().trim().max(500).nullable(),
    isAllDay: z.boolean(),
    start: z.string().min(10),
    end: z.string().min(10),
    timezone: z.string().max(64),
    attendees: z.array(attendeeInput).max(50),
  })
  .partial();
export type UpdateEventInput = z.infer<typeof updateEventSchema>;

// ── Settings ───────────────────────────────────────────────────────────────

export const assistantSettingsSchema = z.object({
  /** Emails or `@domain` suffixes whose mail always lands in the feed. */
  vipSenders: z.array(z.string().trim().min(1).max(200)).max(200).default([]),
  /** Emails or `@domain` suffixes the rules treat as bulk: never in the feed. Fed by the Junk action. */
  mutedSenders: z.array(z.string().trim().min(1).max(200)).max(1000).default([]),
  awaitingReplyDays: z.number().int().min(1).max(30).default(3),
  /** Mail older than this is neither pulled nor kept: the nightly sweep deletes it. Owner: "four weeks". */
  lookbackDays: z.number().int().min(1).max(90).default(28),
  /** Off by default: the owner found the summaries unnecessary and the tokens not worth it. */
  aiEnabled: z.boolean().default(false),
  aiMaxMessagesPerTick: z.number().int().min(0).max(1000).default(100),
  /** Every address the owner sends from; filled in from connections, editable. */
  ownerEmails: z.array(z.string().trim().email()).max(50).default([]),
});
export type AssistantSettings = z.infer<typeof assistantSettingsSchema>;
export const DEFAULT_ASSISTANT_SETTINGS: AssistantSettings = assistantSettingsSchema.parse({});

// ── Tasks ──────────────────────────────────────────────────────────────────

export const TASK_STATUSES = ['open', 'completed'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_PRIORITIES = ['high', 'low'] as const;

export const updateTaskListSchema = z.object({ isSelected: z.boolean() });
export type UpdateTaskListInput = z.infer<typeof updateTaskListSchema>;

export interface AssistantTaskList {
  id: string;
  connectionId: string;
  provider: AssistantProviderKey;
  connectionLabel: string;
  color: string;
  providerListId: string;
  name: string;
  isPrimary: boolean;
  isSelected: boolean;
  canWrite: boolean;
}

export const tasksQuerySchema = z.object({
  status: z.enum(['open', 'completed', 'all']).default('open'),
  /** YYYY-MM-DD; open tasks due on or before this date (plus undated ones when includeUndated). */
  dueBefore: isoDate.optional(),
  connectionIds: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type TasksQuery = z.infer<typeof tasksQuerySchema>;

export interface AssistantTask {
  id: string;
  /** Null for native tasks. */
  connectionId: string | null;
  listId: string | null;
  provider: TaskProviderKey;
  color: string;
  listName: string | null;
  providerTaskId: string | null;
  title: string;
  notes: string | null;
  dueDate: string | null;
  status: TaskStatus;
  completedAt: string | null;
  priority: 'high' | 'low' | null;
  sourceMessageId: string | null;
  matterRef: string | null;
  webLink: string | null;
  canWrite: boolean;
  pendingSync: boolean;
  createdAt: string;
  updatedAt: string;
}

export const createTaskSchema = z.object({
  /** Omit for a native task that lives only in the assistant. */
  listId: z.string().uuid().optional(),
  title: z.string().trim().min(1).max(500),
  notes: z.string().max(10_000).optional(),
  dueDate: isoDate.nullable().optional(),
  priority: z.enum(TASK_PRIORITIES).nullable().optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = z
  .object({
    title: z.string().trim().min(1).max(500),
    notes: z.string().max(10_000).nullable(),
    dueDate: isoDate.nullable(),
    priority: z.enum(TASK_PRIORITIES).nullable(),
    status: z.enum(TASK_STATUSES),
  })
  .partial();
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

// ── Mail / attention feed ──────────────────────────────────────────────────

export const MAIL_FOLDERS = ['inbox', 'sent'] as const;
export type MailFolder = (typeof MAIL_FOLDERS)[number];

/** Gmail over IMAP/SMTP with an app password (no OAuth: Gmail's restricted scopes need a CASA assessment). */
export const imapConnectionSchema = z.object({
  email: z.string().trim().email(),
  appPassword: z.string().min(8).max(200),
  label: z.string().trim().min(1).max(80).optional(),
  imapHost: z.string().trim().min(1).max(200).default('imap.gmail.com'),
  imapPort: z.number().int().min(1).max(65535).default(993),
  smtpHost: z.string().trim().min(1).max(200).default('smtp.gmail.com'),
  smtpPort: z.number().int().min(1).max(65535).default(465),
});
export type ImapConnectionInput = z.infer<typeof imapConnectionSchema>;

/** Carrie's bar app planner + to-do. No credential: the API authenticates as itself. */
export const carriesConnectionSchema = z.object({
  label: z.string().trim().min(1).max(80).optional(),
  /** Override of the bar app's API origin; normally left unset. */
  baseUrl: z.string().trim().url().max(300).optional(),
});
export type CarriesConnectionInput = z.infer<typeof carriesConnectionSchema>;

export const messagesQuerySchema = z.object({
  /** open/snoozed/done = the attention feed; inbox = every synced inbox message (the last `lookbackDays`). */
  state: z.enum(['open', 'snoozed', 'done', 'inbox']).default('open'),
  connectionIds: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type MessagesQuery = z.infer<typeof messagesQuerySchema>;

export interface AssistantMessage {
  id: string;
  connectionId: string;
  provider: AssistantProviderKey;
  color: string;
  connectionLabel: string;
  folder: MailFolder;
  fromEmail: string | null;
  fromName: string | null;
  toEmails: string[];
  ccEmails: string[];
  subject: string;
  bodyPreview: string | null;
  /** Present on the detail endpoint only. */
  bodyText?: string | null;
  receivedAt: string;
  isRead: boolean;
  isFlagged: boolean;
  /** When you answered it: a reply sent from here, your later message in the thread, or the mailbox's answered flag. */
  repliedAt: string | null;
  /** A meeting request is attached; POST /messages/:id/accept-invite puts it on the matching calendar. */
  hasInvite: boolean;
  hasAttachments: boolean;
  webLink: string | null;
  attentionState: AttentionState;
  attentionReasons: string[];
  aiSummary: string | null;
  aiExtracted: AiExtracted | null;
  localState: MessageLocalState;
  snoozedUntil: string | null;
  doneAt: string | null;
  /** Reply / flag / read write back to the provider. */
  canReply: boolean;
}

/** What the AI pulled out of a message (phase 4). Validated server-side before storage. */
export interface AiExtracted {
  category: 'reply' | 'deadline' | 'task' | 'fyi' | 'none';
  needsAction: boolean;
  dates: { iso: string; label: string }[];
  tasks: { title: string; due: string | null }[];
}

/** Lift a task out of a message: either one the AI proposed (by index) or a typed one. */
export const extractTaskSchema = z
  .object({
    taskIndex: z.number().int().min(0).max(9).optional(),
    title: z.string().trim().min(1).max(500).optional(),
    dueDate: isoDate.nullable().optional(),
    listId: z.string().uuid().optional(),
  })
  .refine((v) => v.taskIndex !== undefined || v.title !== undefined, { message: 'taskIndex or title is required' });
export type ExtractTaskInput = z.infer<typeof extractTaskSchema>;

export const replySchema = z.object({
  bodyText: z.string().trim().min(1).max(20_000),
  replyAll: z.boolean().default(false),
});
export type ReplyInput = z.infer<typeof replySchema>;

export const flagSchema = z.object({ flagged: z.boolean() });
/** Junk: move to the provider's Junk folder, mute the sender (or its whole domain), mark done. */
export const junkSchema = z.object({ scope: z.enum(['sender', 'domain']).default('sender') });

export interface AcceptInviteResult {
  /** Where the event landed (the account label). */
  calendarLabel: string;
  /** Set when the event was created here (IMAP invites); Graph accepts server-side and syncs in. */
  eventId: string | null;
  summary: string | null;
}
export type JunkInput = z.infer<typeof junkSchema>;
export const readSchema = z.object({ read: z.boolean() });
export const snoozeSchema = z.object({ until: z.string().datetime({ offset: true }) });
export type SnoozeInput = z.infer<typeof snoozeSchema>;

export const sendMailSchema = z.object({
  connectionId: z.string().uuid(),
  to: z.array(z.string().trim().email()).min(1).max(50),
  cc: z.array(z.string().trim().email()).max(50).default([]),
  subject: z.string().trim().min(1).max(500),
  bodyText: z.string().trim().min(1).max(20_000),
});
export type SendMailInput = z.infer<typeof sendMailSchema>;

// ── Today ──────────────────────────────────────────────────────────────────

export const todayQuerySchema = z.object({
  /** The owner's local date; defaults to UTC today. */
  date: isoDate.optional(),
  /** IANA zone used to bound "today"; defaults to UTC. */
  tz: z.string().max(64).optional(),
});
export type TodayQuery = z.infer<typeof todayQuerySchema>;

export interface AssistantToday {
  date: string;
  events: AssistantEvent[];
  overdue: AssistantTask[];
  dueToday: AssistantTask[];
  /** Open tasks with no date, newest first, capped. */
  undated: AssistantTask[];
  needsReauth: number;
  /** Events in the following six days, for the "coming up" strip. */
  upcoming: AssistantEvent[];
  /** Open attention-feed items, newest first, capped. */
  attention: AssistantMessage[];
  attentionOpen: number;
}

// ── Summary (nav badge) ────────────────────────────────────────────────────

export interface AssistantSummary {
  /** Connections needing the owner to reconnect. */
  needsReauth: number;
  /** Attention-feed items in `open` state (phase 3). */
  attentionOpen: number;
  /** Tasks due today or overdue (phase 2). */
  tasksDue: number;
  /** Events starting in the next 24 hours across every selected calendar. */
  eventsNext24h: number;
}
